import { useCallback, useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Select, Tag, Modal, message } from 'antd';
import { PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import { listOrders, deleteOrder, type Order, type OrderFilters } from '@/api/orderApi';
import { useAuth } from '@/hooks/useAuth';

const ORDER_STATUSES = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'] as const;

const STATUS_COLORS: Record<string, string> = {
  pending: 'default',
  confirmed: 'processing',
  processing: 'blue',
  shipped: 'cyan',
  delivered: 'success',
  cancelled: 'error',
};

export default function OrderListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [filters, setFilters] = useState<OrderFilters>({});

  const canWrite = hasPermission('orders', 'write');
  const canDelete = hasPermission('orders', 'delete');

  const handleFilterChange = useCallback(
    (key: keyof OrderFilters, value: string | undefined) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const fetchData = useCallback(
    (params: SearchParams) => listOrders(params, filters),
    [filters]
  );

  const filterMounted = useRef(false);
  useEffect(() => {
    if (!filterMounted.current) {
      filterMounted.current = true;
      return;
    }
    gridRef.current?.reload();
  }, [filters]);

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('orders.confirm_delete'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteOrder(id);
            message.success(t('orders.delete_success'));
            gridRef.current?.reload();
          } catch (err) {
            message.error(err instanceof Error ? err.message : t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const columns: ColumnsType<Order> = [
    {
      title: t('orders.field.order_number'),
      dataIndex: 'order_number',
      key: 'order_number',
      width: 140,
      render: (val: string) => val || '—',
    },
    {
      title: t('orders.field.title'),
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: t('orders.field.customer_id'),
      dataIndex: 'customer_id',
      key: 'customer_id',
      width: 180,
      ellipsis: true,
    },
    {
      title: t('orders.field.status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (val: string) => (
        <Tag color={STATUS_COLORS[val] ?? 'default'}>
          {t(`orders.status_options.${val}`)}
        </Tag>
      ),
    },
    {
      title: t('orders.field.total'),
      dataIndex: 'total',
      key: 'total',
      width: 130,
      align: 'right',
      render: (val: number, record: Order) =>
        `${record.currency} ${val?.toLocaleString(undefined, { minimumFractionDigits: 2 }) ?? '0.00'}`,
    },
    {
      title: t('orders.field.order_date'),
      dataIndex: 'order_date',
      key: 'order_date',
      width: 130,
      render: (val: string | null) =>
        val ? new Date(val).toLocaleDateString() : '—',
    },
    {
      title: t('orders.field.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 130,
      render: (val: string) => new Date(val).toLocaleDateString(),
    },
    {
      title: t('app.actions'),
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_: unknown, record: Order) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/orders/${record.id}`)}
          />
          {canWrite && (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(`/orders/${record.id}/edit`)}
            />
          )}
          {canDelete && (
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id)}
            />
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Space>
          <Select
            style={{ width: 160 }}
            placeholder={t('orders.filter.status')}
            allowClear
            value={filters.status}
            onChange={(val) => handleFilterChange('status', val)}
            options={ORDER_STATUSES.map((s) => ({
              value: s,
              label: t(`orders.status_options.${s}`),
            }))}
          />
        </Space>
        {canWrite && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/orders/new')}
          >
            {t('orders.new_order')}
          </Button>
        )}
      </div>
      <DataGrid<Order>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchPlaceholder={t('orders.search_placeholder')}
      />
    </div>
  );
}
